import Features from '@Features/feature-flags';
import {
  number,
  bool,
  dateTime,
  str,
  timeseries,
  relation,
  date,
} from 'utils/Studio/SelectOptions';
import { SELF_JOIN_RELATION_NAME } from '@redux/Studio/QueryCreation/constants/queryCreation.constants';

const trunateToInfo = {
  year: 'YYYY',
  month: 'YYYY/MM',
  day: 'YYYY/MM/DD',
  hour: 'YYYY/MM/DD 12PM',
  minute: 'YYYY/MM/DD 12:34PM',
};

const getCalcMap = (calcsList, uri, conceptId) => {
  return calcsList.reduce((acc, calc) => {
    const argsMap = calc.arguments.reduce((argAcc, arg) => {
      if (uri === arg.property && conceptId === arg.concept && arg.aggrType !== null) {
        return {
          ...argAcc,
          [arg.aggrType]: true,
        };
      } else {
        return argAcc;
      }
    }, {});

    return {
      ...acc,
      ...argsMap,
    };
  }, {});
};

export const getPropConfig = (prop, sort = [], hasCalc, calcsList) => {
  let config = [];
  let cardOutput = {};

  if (prop.propTypeLabel && prop.label !== prop.propTypeLabel) {
    config.push({ type: 'Renamed', info: ['From: ' + prop.propTypeLabel] });
    cardOutput = {
      ...cardOutput,
      isRenamed: prop.label,
    };
  }

  if (prop.filters && prop.filters.length > 0) {
    cardOutput = {
      ...cardOutput,
      hasFilters: true,
    };
  }

  if (prop.aggregates && prop.aggregates.length > 0) {
    let calcMap = {};

    if (hasCalc) {
      calcMap = getCalcMap(calcsList, prop.uri, prop.conceptId);
    }

    let info = prop.aggregates.map((agg) => ({
      type: agg.type,
      label: agg.columnLabel || prop.label || '',
      hasCalc: agg.type in calcMap,
    }));

    cardOutput = {
      ...cardOutput,
      hasAggregates: true,
      aggregates: info,
    };
  }

  if (prop?.windowFunctions?.length > 0) {
    const wfInfo = prop.windowFunctions.map((wf) => ({
      type: wf.type,
      label: wf.columnLabel,
    }));

    cardOutput = {
      ...cardOutput,
      windowFunctions: wfInfo,
    };
  }
  if (!prop.optional) {
    config.push({ type: 'Required', info: [] });
  }
  if (prop.hidden) {
    config.push({ type: 'Hidden', info: [] });
  }
  if (prop.groupedBy) {
    config.push({ type: 'Groups results', info: [] });
  }
  if (prop.outputCategory) {
    config.push({ type: 'Output key', info: [prop.outputCategory] });
  }

  if (prop.truncateTo) {
    config.push({
      type: 'Truncate to',
      info: Features.usesSaturn
        ? [prop.truncateTo]
        : [`${prop.truncateTo} (${trunateToInfo[prop.truncateTo]})`],
    });
  }
  if (prop.round !== null && prop.round !== undefined) {
    const type = prop.round === 0 ? 'Round' : 'Precision';
    const info = prop.round === 0 ? [] : [prop.round + ' decimal positions'];
    config.push({ type, info });
  }

  sort &&
    sort.forEach((item) => {
      if (item.elementProps.property === prop.uri && item.elementProps.concept === prop.conceptId) {
        config.push({ type: 'Sort by', info: [`${prop.label} (${item.order})`] });
        cardOutput = {
          ...cardOutput,
          isSorted: true,
        };
      }
    });

  return { ...cardOutput, config };
};

export const getExistingProps = (queryMap) => {
  const { nonRelationList, relationList } = Object.keys(queryMap).reduce(
    (acc, concept) => {
      const conceptData = queryMap[concept];
      const { color, label: conceptName, properties, relations } = conceptData;

      // Process properties
      const processedProperties = properties.map((prop) => ({
        ...prop,
        isExisting: true,
        color,
        conceptId: concept,
        conceptName,
      }));

      // Process relations
      const processedRelations = Object.keys(relations).reduce((relAcc, rel) => {
        const relInstance = relations[rel];
        if (rel !== SELF_JOIN_RELATION_NAME) {
          relAcc.push({
            isRelation: true,
            isExisting: true,
            color,
            conceptId: concept,
            conceptName,
            label: relInstance.label,
            relationTypeUri: relInstance.relationTypeUri,
            uri: relInstance.uri,
            selfJoin: false,
            subQuestionUri: rel.subQueryUri,
            parentQueryId: rel.parentQueryId,
          });
        } else {
          relInstance.relationInstances.forEach((item) => {
            relAcc.push({
              conceptId: concept,
              color,
              label: item.label,
              relationTypeUri: item.relationTypeUri,
              uri: concept,
              selfJoin: true,
              isRelation: true,
              isExisting: true,
              conceptName,
              subQuestionUri: item.subQueryUri,
              parentQueryId: item.parentQueryId,
            });
          });
        }
        return relAcc;
      }, []);

      // Separate properties into non-relations and relations
      acc.nonRelationList.push(...processedProperties);
      acc.relationList.push(...processedRelations);

      return acc;
    },
    { nonRelationList: [], relationList: [] }
  );

  // Sort the lists
  const sortByLabel = (a, b) => {
    const labelA = a.label || '';
    const labelB = b.label || '';
    return labelA.localeCompare(labelB);
  };
  nonRelationList.sort(sortByLabel);
  relationList.sort(sortByLabel);

  // Stack the lists
  const sortedProperties = [...nonRelationList, ...relationList];

  return sortedProperties.reduce((acc, item) => {
    if (
      !item.selfJoin ||
      !checkIfSelfJoinAlreadyExists(item.conceptId, item.relationTypeUri, acc)
    ) {
      return [...acc, item];
    } else {
      return [...acc];
    }
  }, []);
};

export const getPropType = (propUri) => {
  try {
    return propUri.substring(propUri.lastIndexOf('#') + 1, propUri.length);
  } catch (e) {
    if (propUri) {
      console.log('error in getPropType for: ', propUri);
    }
    return null;
  }
};

export const getPropTypeString = (propUri) => {
  const type = getPropType(propUri);

  switch (type) {
    case '':
    case null:
      return '';

    case str:
    case number:
    case bool:
    case date:
    case dateTime:
    case timeseries:
      return type;

    default:
      return relation;
  }
};

export const createNewProp = (updatedObj, uri, properties) => {
  const index = properties.findIndex((prop) => prop.uri === uri);
  return [
    {
      ...properties[index],
      ...updatedObj,
    },
  ];
};

export const removeProp = (uri, properties) => properties.filter((prop) => prop.uri !== uri);

const checkIfSelfJoinAlreadyExists = (conceptId, relationId, instances) => {
  return instances.some(
    (item) => item.conceptId === relationId && item.relationTypeUri === conceptId
  );
};
